import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, tap } from 'rxjs';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { UserCredits } from '../../models/UserCredits';
import { ApiUrlService } from '../api/api-url.service';
import {
  CreditTransaction,
  BusinessCreditsSummary,
  CreditRecipient,
  CharityImpact
} from '../../models/CreditTransaction';

@Injectable({
  providedIn: 'root',
})
export class CreditsService {
  private readonly httpClient: HttpClientWrapperService = inject(HttpClientWrapperService);
  private readonly apiUrlService = inject(ApiUrlService);
  private get creditsApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/credits`); }

  private readonly _creditBalance = signal<number>(0);
  readonly creditBalance: Signal<number> = this._creditBalance.asReadonly();
  readonly creditBalance$ = toObservable(this._creditBalance);

  getUserBalance(userId: string): Observable<UserCredits> {
    return this.httpClient.get<UserCredits>(`${this.creditsApiUrl}/balance/${userId}`).pipe(
      tap((credits: UserCredits) => {
        this._creditBalance.set(credits.credits);
      })
    );
  }

  getUserTransactions(userId: string): Observable<CreditTransaction[]> {
    return this.httpClient.get<CreditTransaction[]>(`${this.creditsApiUrl}/transactions?userId=${userId}`);
  }

  getBusinessCreditsSummary(businessId: number): Observable<BusinessCreditsSummary> {
    return this.httpClient.get<BusinessCreditsSummary>(`${this.creditsApiUrl}/business/${businessId}/summary`);
  }

  getBusinessRecipients(businessId: number): Observable<CreditRecipient[]> {
    return this.httpClient.get<CreditRecipient[]>(`${this.creditsApiUrl}/business/${businessId}/recipients`);
  }

  getBusinessCharitiesImpacted(businessId: number): Observable<CharityImpact[]> {
    return this.httpClient.get<CharityImpact[]>(`${this.creditsApiUrl}/business/${businessId}/charities`);
  }

  updateCreditBalance(credits: number): void {
    this._creditBalance.set(credits);
  }

  getCurrentBalance(): number {
    return this._creditBalance();
  }
}
